import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip } from './index';

const API = process.env.REACT_APP_API_URL;

// Field-level help icon — small "?" that shows a tooltip on hover
function Help({ text }) {
  return (
    <Tooltip text={text}>
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-100 text-slate-400 text-[10px] font-bold cursor-help hover:bg-slate-200 hover:text-slate-600 transition-colors ml-1">?</span>
    </Tooltip>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '',               label: 'All Statuses' },
  { value: 'active',         label: 'Active' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'probation',      label: 'Probation' },
  { value: 'suspended',      label: 'Suspended' },
  { value: 'terminated',     label: 'Terminated' },
  { value: 'inactive',       label: 'Inactive' },
];

const RISK_OPTIONS = [
  { value: '',         label: 'All Risk Levels' },
  { value: 'low',      label: 'Low' },
  { value: 'medium',   label: 'Medium' },
  { value: 'high',     label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const TYPE_OPTIONS = [
  { value: '',             label: 'All Types' },
  { value: 'first_tier',   label: 'First-tier' },
  { value: 'downstream',   label: 'Downstream' },
  { value: 'related',      label: 'Related' },
];

const STATUS_BADGE = {
  active:         'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  pending_review: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  probation:      'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
  suspended:      'bg-red-50 text-red-700 ring-1 ring-red-200',
  terminated:     'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
  inactive:       'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
};

const RISK_BADGE = {
  critical: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  high:     'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
  medium:   'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  low:      'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
};

const TYPE_LABEL = {
  first_tier: 'First-tier',
  downstream: 'Downstream',
  related:    'Related',
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC','PR','GU','VI',
];

const EMPTY_FORM = {
  name: '',
  legal_name: '',
  tax_id: '',
  npi: '',
  entity_type: 'first_tier',
  tier: '',
  address_line1: '',
  city: '',
  state: '',
  zip: '',
  primary_contact_name: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  compliance_officer_name: '',
  compliance_officer_email: '',
  risk_level: 'medium',
  notes: '',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function Badge({ value, map, fallback = '' }) {
  const cls = map[value] || 'bg-slate-100 text-slate-500 ring-1 ring-slate-200';
  const label = fallback || (value ? value.replace(/_/g, ' ') : '—');
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>
      {label}
    </span>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm px-5 py-4 flex flex-col gap-1 min-w-[120px]">
      <span className={`text-2xl font-bold ${accent || 'text-slate-800'}`}>{value ?? '—'}</span>
      <span className="text-xs text-slate-500 font-medium">{label}</span>
    </div>
  );
}

function InputField({ label, id, required, help, children, className }) {
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-xs font-semibold text-slate-600 mb-1.5">
        {label}{required && <span className="text-orange-500 ml-0.5">*</span>}
        {help && <Help text={help} />}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 bg-white transition-colors';

const selectCls =
  'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 bg-white transition-colors appearance-none';

// ─── Create Entity Modal ───────────────────────────────────────────────────────

function CreateEntityModal({ onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const body = { ...form };
      if (body.tier !== '') body.tier = Number(body.tier);
      else delete body.tier;
      // strip empty strings for optional fields
      Object.keys(body).forEach((k) => { if (body[k] === '') delete body[k]; });

      const res = await fetch(`${API}/api/fdr/entities`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Error ${res.status}`);
      }
      const created = await res.json();
      onCreated(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200/60 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Add FDR Entity</h2>
            <p className="text-sm text-slate-400 mt-0.5">Register a new first-tier, downstream, or related entity</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

          {/* Identity */}
          <section>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Identity</p>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Entity Name" id="name" required className="col-span-2">
                <input
                  id="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={set('name')}
                  placeholder="DBA or operating name"
                  className={inputCls}
                />
              </InputField>

              <InputField label="Legal Name" id="legal_name">
                <input
                  id="legal_name"
                  type="text"
                  value={form.legal_name}
                  onChange={set('legal_name')}
                  placeholder="Full legal entity name"
                  className={inputCls}
                />
              </InputField>

              <InputField label="Tax ID (EIN)" id="tax_id" help="The entity's Employer Identification Number. Used to uniquely identify the organization and prevent duplicates.">
                <input
                  id="tax_id"
                  type="text"
                  value={form.tax_id}
                  onChange={set('tax_id')}
                  placeholder="XX-XXXXXXX"
                  className={inputCls}
                />
              </InputField>

              <InputField label="NPI" id="npi" help="National Provider Identifier — only needed if this entity is a healthcare provider. Used for LEIE exclusion screening matching.">
                <input
                  id="npi"
                  type="text"
                  value={form.npi}
                  onChange={set('npi')}
                  placeholder="10-digit NPI"
                  className={inputCls}
                />
              </InputField>

              <InputField label="Entity Type" id="entity_type" required help="First-tier: contracts directly with your plan. Downstream: subcontracted by a first-tier entity. Related: shares common ownership or control with your plan.">
                <select id="entity_type" value={form.entity_type} onChange={set('entity_type')} className={selectCls}>
                  <option value="first_tier">First-tier</option>
                  <option value="downstream">Downstream</option>
                  <option value="related">Related</option>
                </select>
              </InputField>

              <InputField label="Tier (1-5)" id="tier" help="Tier 1 = direct relationship with your plan. Higher tiers = further down the subcontracting chain. Most entities are Tier 1 or 2.">
                <select id="tier" value={form.tier} onChange={set('tier')} className={selectCls}>
                  <option value="">— None —</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </InputField>

              <InputField label="Risk Level" id="risk_level" required help="Your initial risk assessment for this entity. Low = routine oversight. High/Critical = more frequent assessments and closer monitoring. This will be updated automatically after assessments.">
                <select id="risk_level" value={form.risk_level} onChange={set('risk_level')} className={selectCls}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </InputField>
            </div>
          </section>

          {/* Address */}
          <section>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Address</p>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Address Line 1" id="address_line1" className="col-span-2">
                <input
                  id="address_line1"
                  type="text"
                  value={form.address_line1}
                  onChange={set('address_line1')}
                  placeholder="Street address"
                  className={inputCls}
                />
              </InputField>

              <InputField label="City" id="city">
                <input
                  id="city"
                  type="text"
                  value={form.city}
                  onChange={set('city')}
                  placeholder="City"
                  className={inputCls}
                />
              </InputField>

              <div className="grid grid-cols-2 gap-3">
                <InputField label="State" id="state">
                  <select id="state" value={form.state} onChange={set('state')} className={selectCls}>
                    <option value="">—</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </InputField>

                <InputField label="ZIP" id="zip">
                  <input
                    id="zip"
                    type="text"
                    value={form.zip}
                    onChange={set('zip')}
                    placeholder="00000"
                    className={inputCls}
                  />
                </InputField>
              </div>
            </div>
          </section>

          {/* Primary Contact */}
          <section>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Primary Contact</p>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Name" id="primary_contact_name" className="col-span-2">
                <input
                  id="primary_contact_name"
                  type="text"
                  value={form.primary_contact_name}
                  onChange={set('primary_contact_name')}
                  placeholder="Full name"
                  className={inputCls}
                />
              </InputField>

              <InputField label="Email" id="primary_contact_email">
                <input
                  id="primary_contact_email"
                  type="email"
                  value={form.primary_contact_email}
                  onChange={set('primary_contact_email')}
                  placeholder="email@example.com"
                  className={inputCls}
                />
              </InputField>

              <InputField label="Phone" id="primary_contact_phone">
                <input
                  id="primary_contact_phone"
                  type="tel"
                  value={form.primary_contact_phone}
                  onChange={set('primary_contact_phone')}
                  placeholder="(555) 000-0000"
                  className={inputCls}
                />
              </InputField>
            </div>
          </section>

          {/* Compliance Officer */}
          <section>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Compliance Officer</p>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Name" id="compliance_officer_name">
                <input
                  id="compliance_officer_name"
                  type="text"
                  value={form.compliance_officer_name}
                  onChange={set('compliance_officer_name')}
                  placeholder="Full name"
                  className={inputCls}
                />
              </InputField>

              <InputField label="Email" id="compliance_officer_email">
                <input
                  id="compliance_officer_email"
                  type="email"
                  value={form.compliance_officer_email}
                  onChange={set('compliance_officer_email')}
                  placeholder="email@example.com"
                  className={inputCls}
                />
              </InputField>
            </div>
          </section>

          {/* Notes */}
          <section>
            <InputField label="Notes" id="notes">
              <textarea
                id="notes"
                rows={3}
                value={form.notes}
                onChange={set('notes')}
                placeholder="Any additional context or notes..."
                className={`${inputCls} resize-none`}
              />
            </InputField>
          </section>

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-entity-form"
            disabled={saving}
            onClick={handleSubmit}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-sm shadow-orange-500/20 transition-all disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Create Entity'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function EntityRegistry({ onOpenEntity }) {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search)       params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (riskFilter)   params.set('risk_level', riskFilter);
      if (typeFilter)   params.set('entity_type', typeFilter);

      const qs = params.toString();
      const res = await fetch(`${API}/api/fdr/entities${qs ? `?${qs}` : ''}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setEntities(Array.isArray(data) ? data : data.entities || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, riskFilter, typeFilter]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  // Stats
  const stats = {
    total:          entities.length,
    active:         entities.filter((e) => e.status === 'active').length,
    pending_review: entities.filter((e) => e.status === 'pending_review').length,
    probation:      entities.filter((e) => e.status === 'probation').length,
    suspended:      entities.filter((e) => e.status === 'suspended').length,
  };

  const handleCreated = (entity) => {
    setShowCreate(false);
    fetchEntities();
    if (entity?.id) onOpenEntity(entity.id);
  };

  const formatDate = (val) => {
    if (!val) return '—';
    // DATE columns arrive as "YYYY-MM-DD" or UTC-midnight ISO strings; both
    // render a day early in US timezones. Pin them to local midnight.
    const v = typeof val === 'string' && /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/.test(val)
      ? `${val.slice(0, 10)}T00:00:00` : val;
    try { return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return val; }
  };

  return (
    <div className="p-8 space-y-6">

      {/* Stats Bar */}
      <div className="flex flex-wrap gap-3">
        <StatCard label="Total Entities"   value={stats.total}          accent="text-slate-800" />
        <StatCard label="Active"           value={stats.active}         accent="text-emerald-600" />
        <StatCard label="Pending Review"   value={stats.pending_review} accent="text-amber-600" />
        <StatCard label="Probation"        value={stats.probation}      accent="text-orange-600" />
        <StatCard label="Suspended"        value={stats.suspended}      accent="text-red-600" />
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm px-5 py-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name or tax ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-colors"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 bg-white transition-colors min-w-[150px]"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Risk filter */}
          <select
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
            className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 bg-white transition-colors min-w-[150px]"
          >
            {RISK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Type filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 bg-white transition-colors min-w-[150px]"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Add Entity */}
          <button
            onClick={() => setShowCreate(true)}
            className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-sm shadow-orange-500/20 transition-all whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Entity
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
            <svg className="w-5 h-5 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Loading entities...
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <span className="text-red-500 text-sm font-medium">{error}</span>
            <button
              onClick={fetchEntities}
              className="text-sm text-orange-600 hover:text-orange-700 font-semibold underline underline-offset-2"
            >
              Retry
            </button>
          </div>
        ) : entities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2 text-slate-400">
            <svg className="w-10 h-10 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <p className="text-sm font-medium">No entities found</p>
            <p className="text-xs">Adjust filters or add a new entity</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3.5">Name</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3.5">Type</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3.5">Status</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3.5">Risk</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3.5">Contracts</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3.5">Del. Functions</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3.5">Open Findings</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3.5">This Cycle</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3.5">Next Assessment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entities.map((entity) => (
                  <tr
                    key={entity.id}
                    onClick={() => onOpenEntity(entity.id)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-800 group-hover:text-orange-700 transition-colors">
                        {entity.name}
                      </div>
                      {entity.tax_id && (
                        <div className="text-xs text-slate-400 mt-0.5">EIN: {entity.tax_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {TYPE_LABEL[entity.entity_type] || entity.entity_type || '—'}
                      {entity.tier != null && (
                        <span className="ml-1.5 text-xs text-slate-400">Tier {entity.tier}</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <Badge value={entity.status} map={STATUS_BADGE} />
                    </td>
                    <td className="px-4 py-4">
                      <Badge value={entity.risk_level} map={RISK_BADGE} />
                    </td>
                    {/* Empty is a fact, not a blank: every cell says what
                        "nothing" means so the reader never guesses. */}
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      {(entity.active_contracts_count ?? entity.contracts_count) > 0 ? (
                        <span className="text-slate-700 font-medium tabular-nums">{entity.active_contracts_count ?? entity.contracts_count}</span>
                      ) : (entity.contracts_count > 0 ? (
                        <span className="text-[11px] font-medium text-amber-600">{entity.contracts_count} not active</span>
                      ) : (
                        <span className="text-[11px] text-slate-400">none on file</span>
                      ))}
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      {entity.delegated_functions_count > 0 ? (
                        <span className="text-slate-700 font-medium tabular-nums">{entity.delegated_functions_count}</span>
                      ) : (
                        <span className="text-[11px] text-slate-400">none recorded</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      {entity.open_findings_count > 0 ? (
                        <span className="font-semibold text-red-600 tabular-nums">{entity.open_findings_count}</span>
                      ) : (
                        <span className="text-[11px] font-medium text-emerald-600">none open</span>
                      )}
                    </td>
                    {/* Where this vendor stands in the current assessment
                        cycle — the registry answers it without a click. */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(() => {
                        const a = entity.latest_assessment;
                        if (!a) return <span className="text-[11px] font-medium text-amber-600">not started</span>;
                        if (a.status === 'closed') return <span className="text-[11px] font-semibold text-emerald-700">Closed{a.score != null ? ` · ${Math.round(a.score)}%` : ''}</span>;
                        if (a.report_final_at) return <span className="text-[11px] font-semibold text-emerald-700">Final issued</span>;
                        if (a.report_sent_at) return <span className="text-[11px] font-semibold text-violet-700">Awaiting responses</span>;
                        if (a.submitted_at) return <span className="text-[11px] font-semibold text-sky-700">In review</span>;
                        if (a.sent_at) return <span className="text-[11px] font-semibold text-amber-700">Awaiting vendor</span>;
                        return <span className="text-[11px] font-medium text-slate-500">Preparing</span>;
                      })()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {entity.next_assessment_due ? (
                        <span className="text-slate-500">{formatDate(entity.next_assessment_due)}</span>
                      ) : (
                        <span className="text-[11px] font-medium text-amber-600">not scheduled</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <CreateEntityModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
